import { useEffect, useState } from 'react';
import { Box, Collapse, Divider, Theme } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { getFocusedDifference } from 'store';
import { GVTypography } from 'components/lib';
import { makeStyles } from 'tss-react/mui';
import { getStyleVariables } from 'styles/vars';
import { BarcodeDetails, BarcodeMetadataItem, Difference, DifferenceTypeNames } from 'types';
import {
  getCategoryColor,
  getGradeCategory,
  validateAndNormalizePassingStatus,
} from 'pdftron/utils/barcodeGradeColors';
import DecodedLayout from './DecodedLayout';
import { processHumanReadableStatus, processResolvedUrlStatus } from './barcodeStatusUtils';
import { partitionBarcodeMetadata } from './barcodeMetadataGrouping';

const metadataLabels: { [key: string]: string } = {
  barcodeType: 'Type',
  grade: 'Grade',
  decodability: 'Decodability',
  magnification: 'Magnification',
  symbolContrast: 'Symbol Contrast',
  minEdgeContrast: 'Min Edge Contrast',
  modulation: 'Modulation',
  defects: 'Defects',
  decode: 'Decode',
  quietZone: 'Quiet Zone',
  narrowBar: 'Narrow Bar',
  barRatio: 'Wide To Narrow Ratio',
  wideBar: 'Wide Bar',
  gap: 'Gap',
  structure: 'Data Structure',
  minReflectance: 'Min Reflectance',
  maxReflectance: 'Max Reflectance',
  barWidthReduction: 'BWR',
  barcodeSize: 'Barcode Size',
  version: 'Version',
  cellSize: 'Cell Size',
  gridNonuniformity: 'Grid Non-Uniformity',
  axialNonuniformity: 'Axial Non-Uniformity',
  fixedPatternDamage: 'Fixed Pattern Damage',
  unusedErrorCorrection: 'Unused Error Correction',
  errorCorrectionLevel: 'Error Correction Level',
  barWidthReductionVertical: 'BWR Vertical',
  barWidthReductionHorizontal: 'BWR Horizontal',
  skew: 'Skew',
  tilt: 'Tilt',
  pitch: 'Pitch',
  barWidth: 'Bar Width',
  barHeight: 'Bar Height',
  clearZone: 'Clear Zone',
  spaceSize: 'Space Size',
  barRotation: 'Bar Rotation',
  insideLength: 'Inside Length',
  barContinuity: 'Bar Continuity',
  fullBarHeight: 'Full Bar Height',
  outsideLength: 'Outside Length',
  trackerHeight: 'Tracker Height',
  clearZoneReflectance: 'Clear Zone Reflectance',
  printReflectanceDifference: 'Print Reflectance Difference',
  rows: 'Rows',
  columns: 'Columns',
  humanReadableStatus: 'Human Readable',
  resolvedUrlStatus: 'Resolved URL',
};

// CTF display strings are "<letter> (<measurement>)" for graded parameters (e.g. "A (98.44 %)"),
// a bare grade letter when there is no measurement (e.g. "F"), or a plain measurement for
// non-graded parameters (e.g. "11.58 mm", "N/A").
const GRADED_DISPLAY_REGEX = /^([A-Za-z]{1,3})\s*\((.*)\)$/s;

export const parseGradeDisplay = (value: string): { letter?: string; measurement: string } => {
  if (!value) return { measurement: '' };
  const match = value.match(GRADED_DISPLAY_REGEX);
  if (match) return { letter: match[1], measurement: match[2] };
  // A bare single letter is a grade with no separate measurement (show the letter on both sides).
  if (/^[A-Za-z]$/.test(value)) return { letter: value, measurement: value };
  return { measurement: value };
};

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    sectionHeader: {
      fontFamily: '"Open Sans"',
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '100%',
      letterSpacing: 0,
      textTransform: 'uppercase',
      color: '#929BB2',
      margin: theme.spacing(2, 0, 1, 0),
    },
    requiredHeader: {
      fontFamily: '"Open Sans"',
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '100%',
      letterSpacing: 0,
      textTransform: 'uppercase',
      color: '#929BB2',
      margin: 0,
      marginBottom: '8px',
    },
    summaryBox: {
      backgroundColor: styleVariables.colors.darkGreyBackground,
      borderRadius: '4px',
      padding: theme.spacing(1.5, 1, 1, 1),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: '8px',
      width: '100%',
    },
    summaryType: {
      color: styleVariables.colors.primaryTextColor,
    },
    gradeBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      color: styleVariables.colors.primaryTextColor,
      fontFamily: '"Open Sans"',
      fontWeight: 700,
      lineHeight: 1,
      flexShrink: 0,
    },
    metadataBox: {
      backgroundColor: styleVariables.colors.darkGreyBackground,
      borderRadius: '4px',
      padding: '4px',
      width: '100%',
    },
    rowMain: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: theme.spacing(1),
      borderRadius: '4px',
    },
    rowLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: 0,
    },
    isoPill: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '4px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      color: 'rgba(255, 255, 255, 0.75)',
      fontFamily: '"Open Sans"',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: '16px',
      padding: '0 4px',
      flexShrink: 0,
    },
    severityIcon: {
      width: '14px',
      height: '14px',
      flexShrink: 0,
    },
    rowValue: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
      marginLeft: '8px',
    },
    noteToggle: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
      height: '16px',
      width: '16px',
      flexShrink: 0,
      borderRadius: '4px',
      backgroundColor: '#333539',
    },
    chevron: {
      height: '12px',
      width: '12px',
      color: 'rgba(255, 255, 255, 0.87)',
    },
    rowNote: {
      display: 'block',
      color: 'rgba(255, 255, 255, 0.6)',
      padding: theme.spacing(0, 1, 1, 1),
      fontSize: '11px',
      lineHeight: '16px',
      fontWeight: 600,
      letterSpacing: 0,
    },
    divider: {
      backgroundColor: '#313337',
    },
  };
});

export interface BarcodeLayoutProps {
  id?: string;
}

const BarcodeLayout = ({ id }: BarcodeLayoutProps) => {
  const { classes } = useStyles();
  const currentDifference = useSelector(getFocusedDifference);
  const [collapsedNotes, setCollapsedNotes] = useState<Record<string, boolean>>({});
  // Reset collapsed state when the user navigates to a different barcode difference.
  useEffect(() => {
    setCollapsedNotes({});
  }, [currentDifference?.id]);

  const { barcodeDetails } = (currentDifference as Difference) ?? {};
  if (!barcodeDetails) return null;

  const { decodedValue, barcodeMetadata } = barcodeDetails as BarcodeDetails;

  const {
    primary: primaryMetadata,
    critical: requiredMetadata,
    supplementary: supplementaryMetadata,
    hasContributionData,
    overallPassingStatus,
  } = partitionBarcodeMetadata(barcodeMetadata);

  const hasSections = requiredMetadata.length > 0 || supplementaryMetadata.length > 0;
  const gradeItem = primaryMetadata.find((item) => item.key === 'grade');
  const typeItem = primaryMetadata.find((item) => item.key === 'barcodeType');

  const toggleNote = (key: string) => setCollapsedNotes((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderGradeBadge = (letter: string, passingStatus?: string, size = 24) => {
    const color = getCategoryColor(getGradeCategory(letter, passingStatus));
    return (
      <Box
        className={classes.gradeBadge}
        sx={{
          backgroundColor: color,
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          fontSize: size >= 36 ? '22px' : '12px',
        }}
      >
        {letter}
      </Box>
    );
  };

  const renderRow = (item: BarcodeMetadataItem, isLast: boolean, isRequired: boolean) => {
    const { key, value, passingStatus, statusDescription, contribution } = item;

    // Human Readable / Resolved URL are opt-in params whose value + message arrive as a JSON
    // string. Parse them so they render like every other supplementary row: value on the right and
    // the message as the inline expandable note.
    let displayValue = value;
    let noteText = statusDescription;
    if (key === 'humanReadableStatus' || key === 'resolvedUrlStatus') {
      const result =
        key === 'humanReadableStatus' ? processHumanReadableStatus(value) : processResolvedUrlStatus(value);
      displayValue = result.displayValue;
      noteText = result.statusDescription || statusDescription;
    }

    const isNA = displayValue === 'N/A';
    // Only Required graded params show a left-hand severity icon (warning triangle / fail circle);
    // passes and all supplementary rows show none. The ISO pill marks ISO-mandated params
    // (profile-driven params live in Required too, but carry no pill).
    const severityCategory = isRequired ? validateAndNormalizePassingStatus(passingStatus) : null;
    const showSeverityIcon = severityCategory === 'fail' || severityCategory === 'warning';
    const SeverityIcon = severityCategory === 'fail' ? ErrorIcon : WarningIcon;
    const showIsoPill = isRequired && contribution === 'ISO';
    const hasNote = !!noteText;
    const expanded = !collapsedNotes[key];

    return (
      <Box key={key}>
        <Box className={classes.rowMain}>
          <Box className={classes.rowLabel}>
            <GVTypography emphasis="superhigh" variant="subtitle1">
              {metadataLabels[key] ?? key}
            </GVTypography>
            {showSeverityIcon && severityCategory && (
              <SeverityIcon
                className={classes.severityIcon}
                style={{ color: getCategoryColor(severityCategory) }}
                data-testid="required-severity-icon"
              />
            )}
            {showIsoPill && <span className={classes.isoPill}>ISO</span>}
          </Box>
          <Box className={classes.rowValue}>
            <GVTypography emphasis={isNA ? 'disabled' : 'high'} variant="subtitle1">
              {displayValue}
            </GVTypography>
            {hasNote && (
              <Box
                className={classes.noteToggle}
                onClick={() => toggleNote(key)}
                role="button"
                aria-expanded={expanded}
                data-testid={`barcode-note-toggle-${key}`}
              >
                <ExpandMoreIcon
                  className={classes.chevron}
                  style={{
                    transform: expanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.25s ease',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        {hasNote && (
          <Collapse in={expanded} timeout={250}>
            <Box sx={{ opacity: expanded ? 1 : 0, transition: 'opacity 0.25s ease' }}>
              <GVTypography emphasis="superhigh" variant="subtitle1" className={classes.rowNote}>
                {noteText}
              </GVTypography>
            </Box>
          </Collapse>
        )}
        {!isLast && <Divider className={classes.divider} />}
      </Box>
    );
  };

  const renderMetadataBox = (items: BarcodeMetadataItem[], isRequired: boolean, marginTop?: string) =>
    items.length > 0 ? (
      <Box className={classes.metadataBox} sx={marginTop ? { marginTop } : undefined}>
        {items.map((item, i) => renderRow(item, i === items.length - 1, isRequired))}
      </Box>
    ) : null;

  const summaryBlock =
    gradeItem || (typeItem && typeItem.value !== 'N/A') ? (
      <>
        <GVTypography emphasis="superhigh" variant="body1" className={classes.sectionHeader}>
          Summary
        </GVTypography>
        <Box className={classes.summaryBox}>
          {gradeItem &&
            (() => {
              const { letter } = parseGradeDisplay(gradeItem.value);
              const passingStatus = gradeItem.passingStatus ?? overallPassingStatus;
              return letter ? renderGradeBadge(letter, passingStatus, 36) : null;
            })()}
          {typeItem && typeItem.value !== 'N/A' && (
            <GVTypography emphasis="superhigh" variant="subtitle1" className={classes.summaryType}>
              {typeItem.value}
            </GVTypography>
          )}
        </Box>
      </>
    ) : null;

  const renderSections = (): React.ReactNode => {
    if (!hasSections) return null;

    if (!hasContributionData) {
      return renderMetadataBox(supplementaryMetadata, false, '8px');
    }

    return (
      <>
        {requiredMetadata.length > 0 && (
          <>
            <GVTypography emphasis="superhigh" variant="body1" className={classes.requiredHeader}>
              Required
            </GVTypography>
            {renderMetadataBox(requiredMetadata, true)}
          </>
        )}
        {supplementaryMetadata.length > 0 && (
          <>
            <GVTypography emphasis="superhigh" variant="body1" className={classes.sectionHeader}>
              Supplementary (Unaffecting Grade)
            </GVTypography>
            {renderMetadataBox(supplementaryMetadata, false)}
          </>
        )}
      </>
    );
  };

  return (
    <DecodedLayout
      id={id}
      type={DifferenceTypeNames.Barcode}
      decodedValue={decodedValue}
      staticMetaData={summaryBlock}
      metaData={hasSections ? renderSections : null}
      hideMetaDataTitle
    />
  );
};

export default BarcodeLayout;
